import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Field } from "react-final-form";

import { Card, Stack, useTheme } from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { CommunicationDto } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { User } from "@stores/core/models/User.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Fieldset } from "@ui-components/form/Fieldset/Fieldset.tsx";
import { PhoneTextField } from "@ui-components/form/PhoneTextField.tsx";
import { SubmissionForm } from "@ui-components/form/submission-form/SubmissionForm.tsx";
import { TextInputField } from "@ui-components/form/TextInputField.tsx";
import { When } from "@ui-components/withPerm.tsx";

import { getUserStylesSet } from "../UserScreens.styles.tsx";
import { ProviderSignatureField } from "./ProviderSignatureField.tsx";
import {
  userCommunicationsFormNameOf as nameOf,
  UserCommunicationsFormValues
} from "./UserCommunicationsForm.types.ts";
import { mergeWithCommunications } from "./UserCommunicationsForm.utils.ts";
import { UserCommunicationsFormValidator } from "./UserCommunicationsFormValidator.tsx";

interface UserCommunicationsCardProps {
  user: User;
}

export const UserCommunicationsCard = observer(
  ({ user }: UserCommunicationsCardProps) => {
    const { core, practice } = useStores();
    const theme = useTheme();
    const { formFields, formFooter } = getUserStylesSet(theme);

    const [signatureStagingId, setSignatureStagingId] = useState<string>();
    const [clearedSignature, setClearedSignature] = useState<boolean>();

    const initialValues: UserCommunicationsFormValues = {
      smsName: user.dto.smsName === user.fullName ? "" : user.dto.smsName,
      workPhone: user.workPhone,
      email: user.username,
      signatureUrl: practice.providersMap.get(user.id)?.signatureUrl
    };

    const onSubmit = async (values: UserCommunicationsFormValues) => {
      const communications: CommunicationDto[] = mergeWithCommunications(
        values.workPhone,
        user
      );

      await core.updateUser({
        id: user.id,
        smsName: values.smsName || user.fullName,
        communications
      });

      // ** SIGNATURES **

      // Upload signature
      if (signatureStagingId) {
        await practice.updateSignature({
          fileStagingId: signatureStagingId,
          entityId: user.id
        });
      }

      // Clearing/Deleting signature
      if (clearedSignature) {
        await practice.deleteSignature(user.id);
        setClearedSignature(false);
      }

      setSignatureStagingId(undefined);
      setClearedSignature(false);
    };

    return (
      <Card
        iconName="CannedChat"
        headingLevel="section-heading"
        heading="Communications"
        styles={{
          subComponentStyles: {
            tile: {
              content: {
                padding: 0
              }
            }
          }
        }}
      >
        <SubmissionForm<UserCommunicationsFormValues>
          formName="user-communications"
          validate={values =>
            new UserCommunicationsFormValidator().validate(values)
          }
          readOnly={!core.hasPermissions(Permission.UserWrite)}
          initialValues={initialValues}
          onSubmit={onSubmit}
          buttonsProps={{
            disableCancelOnPristine: true,
            styles: {
              root: formFooter
            }
          }}
        >
          {({ values, form }) => (
            <Fieldset styles={{ root: formFields }}>
              <Stack>
                <When permission={Permission.SmsNamePrerel}>
                  <TextInputField
                    name={nameOf("smsName")}
                    label="Name used in SMS"
                    placeholder="Title, first, and last name if not specified"
                  />
                </When>
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                  <Stack.Item styles={{ root: { flexGrow: 1, flexBasis: 0 } }}>
                    <PhoneTextField name={nameOf("workPhone")} label="Phone" />
                  </Stack.Item>
                  <Stack.Item styles={{ root: { flexGrow: 3, flexBasis: 0 } }}>
                    <TextInputField
                      name={nameOf("email")}
                      label="Email"
                      disabled
                    />
                  </Stack.Item>
                </Stack>
                <ProviderSignatureField
                  label="Signature"
                  name={nameOf("signatureFile")}
                  signatureUrl={values.signatureUrl}
                  onSignatureCleared={() => {
                    form.batch(() => {
                      form.change("signatureFile", undefined);
                      form.change("signatureUrl", undefined);
                    });

                    setSignatureStagingId(undefined);
                    setClearedSignature(true);
                  }}
                  onFileAccepted={stagingId => {
                    form.batch(() => {
                      form.change("signatureUrl", stagingId);
                    });

                    setSignatureStagingId(stagingId);
                    setClearedSignature(false);
                  }}
                />
                <Field name={nameOf("signatureUrl")} render={() => null} />
              </Stack>
            </Fieldset>
          )}
        </SubmissionForm>
      </Card>
    );
  }
);
